import React from 'react';
import { Baby, Shield, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import AllPetCardSection from '../components/AllPetCardSection';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const PuppyProgram = () => {
  const programIncludes = [
    'Confidence building in new environments',
    'Basic obedience foundations',
    'Socialization with people and other dogs',
    'Introduction to farm and ranch life',
    'Crate training and house manners',
    'Leash manners and recall basics',
  ];

  const benefits = [
    {
      icon: <Shield size={32} />,
      title: 'Confidence Building',
      description: 'We help your puppy build confidence through positive exposure to new environments, sounds, and experiences on the farm.'
    },
    {
      icon: <Baby size={32} />,
      title: 'Basic Obedience',
      description: 'Your puppy will learn foundational commands and manners that set them up for a lifetime of good behavior.'
    },
    {
      icon: <Heart size={32} />,
      title: 'Home Environment',
      description: 'Your puppy stays in our home — not a kennel — becoming part of the family while they learn.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Puppy Board & Train Oklahoma — 2-Week Confidence Building & Basic Obedience | Tulsa, Broken Arrow, Owasso</title>
        <meta name="description" content="Professional puppy board and train in Oklahoma. Our 2-week program builds confidence and teaches basic obedience in a real farm environment. Your puppy stays in our home with daily farm and horse exposure. Serving Tulsa, Broken Arrow, Owasso, Jenks, Bixby, Sand Springs, Claremore, Sapulpa, Bartlesville, and surrounding Oklahoma cities. Book a free consultation today!" />
        <link rel="canonical" href="https://farmdogoklahoma.com/puppy-program" />
        <meta property="og:title" content="Puppy Board & Train — 2-Week Confidence & Obedience Program | Farm Dog Oklahoma" />
        <meta property="og:description" content="Professional 2-week puppy board and train in Oklahoma. Confidence building, basic obedience, farm exposure. Your puppy stays in our home, not a kennel." />
        <meta property="og:url" content="https://farmdogoklahoma.com/puppy-program" />
        <meta name="keywords" content="puppy board and train Oklahoma, puppy board and train Tulsa, puppy training Broken Arrow, puppy obedience Owasso, puppy confidence building, puppy socialization Oklahoma, puppy farm training, puppy board and train near me, best puppy training Tulsa, 2 week puppy program, puppy training Jenks, puppy training Bixby, puppy training Sand Springs, puppy training Claremore" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Puppy Board & Train Program",
            "provider": { "@type": "LocalBusiness", "name": "Farm Dog Oklahoma", "telephone": "+1-918-992-7332" },
            "description": "Professional 2-week puppy board and train program focused on confidence building and basic obedience. Your puppy stays in our home with daily farm and horse exposure.",
            "areaServed": ["Tulsa", "Broken Arrow", "Owasso", "Jenks", "Bixby", "Sand Springs", "Claremore", "Sapulpa", "Bartlesville", "Oklahoma"],
            "url": "https://farmdogoklahoma.com/puppy-program"
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What age should my puppy start board and train?", "acceptedAnswer": { "@type": "Answer", "text": "Puppies can start our 2-week board and train program as early as they enter their farm dog era. Early training is crucial for building confidence and foundational skills." }},
              { "@type": "Question", "name": "Where does my puppy stay during the board and train?", "acceptedAnswer": { "@type": "Answer", "text": "Your puppy stays in our home, not a kennel. They become part of the family and get daily exposure to farm life through our partnership with All Round Equine." }},
              { "@type": "Question", "name": "What does the puppy program include?", "acceptedAnswer": { "@type": "Answer", "text": "Our 2-week program includes confidence building, basic obedience foundations, socialization, introduction to farm and ranch life, crate training, house manners, and leash manners." }}
            ]
          }
        `}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5C4033] to-[#3E2723] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Have a Puppy?
            </h1>
            <p className="text-xl md:text-2xl text-[#C4A052] font-semibold">
              2-Week Puppy Board & Train for Confidence & Basic Obedience
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div variants={fadeInUp}>
              <img
                src="/assets/images/11WE7aSXvBno1mLmx2Gkgxb7JUXKL7zfN.jpg"
                alt="Puppy training on farm"
                className="rounded-2xl shadow-2xl w-full h-96 object-cover object-center"
              />
            </motion.div>
            <motion.div variants={fadeInUp} className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                2-Week Puppy Board & Train
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our Puppy Program is designed for puppies just entering their farm dog era. In just two weeks, we focus on building your puppy's confidence and teaching basic obedience — giving them the foundation they need to thrive on the farm and at home.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Your puppy stays in our home, not a kennel, and gets daily exposure to farm life through our partnership with All Round Equine. This real-world experience sets them up for success from day one.
              </p>
            </motion.div>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-3 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="hover-lift border-none shadow-lg">
                  <CardContent className="p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white mb-4">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-bold text-[#5C4033] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {benefit.title}
                    </h3>
                    <p className="text-gray-700">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* What's Included */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              What's Included
            </h2>
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 gap-4">
              {programIncludes.map((item, index) => (
                <motion.div key={index} variants={fadeInUp} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow hover-lift">
                  <div className="w-8 h-8 bg-[#C4A052] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <span className="text-gray-700">{item}</span>
                </motion.div>
              ))}
            </motion.div>
            <div className="mt-8 text-center">
              <p className="text-gray-700 italic">
                This comprehensive 2-week program gives your puppy the confidence and skills to thrive — on the farm and at your side.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* All Pet Card Section */}
      <AllPetCardSection />

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#C4A052] to-[#B8935E] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Start Your Puppy's Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Give your puppy the best start with our 2-week confidence building and basic obedience program!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/contact"
            >
              <Button className="bg-white text-[#5C4033] hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Book Free Consult <ArrowRight className="ml-2" />
              </Button>
            </a>
            <a href="tel:+19189927332">
              <Button className="bg-[#5C4033] text-white hover:bg-[#3E2723] font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Call (918) 992-7332
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PuppyProgram;
